import React from 'react'

import { Button } from '@/shared/components/Button/Button'
import { Icon } from '@/shared/components/Icon/Icon'
import { Modal } from '@/shared/components/Modal/Modal'
import { DialogComponentProps } from '@/shared/components/Modal/models/DialogStructure'
import { pxToRem } from '@/shared/styles/themes'
import { faGem, faInfoCircle } from '@fortawesome/pro-regular-svg-icons'
import { Box, styled, Typography } from '@mui/material'

import { useSubscription } from './SubscriptionContext'

export type SubscriptionUpgradeDialogProps = DialogComponentProps & {
  feature?: string
  reason?: string
  showVehicleLimit?: boolean
  onNavigateToUpgrade?: () => void
  onNavigateToManage?: () => void
}

export const SubscriptionUpgradeDialog: React.FC<SubscriptionUpgradeDialogProps> = ({
  open,
  onClose,
  feature = 'feature',
  reason = 'You have reached your plan limits.',
  showVehicleLimit = false,
  onNavigateToUpgrade,
  onNavigateToManage,
}) => {
  const { subscription } = useSubscription()

  const handleUpgrade = () => {
    onClose()

    if (onNavigateToUpgrade) {
      onNavigateToUpgrade()
    } else {
      window.location.href = '/upgrade'
    }
  }

  const handleManageSubscription = () => {
    onClose()

    if (onNavigateToManage) {
      onNavigateToManage()
    } else {
      window.location.href = '/subscription/manage'
    }
  }

  const getUpgradeMessage = () => {
    if (showVehicleLimit && subscription) {
      return `You've reached your vehicle limit (${subscription.currentVehicleCount}/${subscription.vehicleQuantity}). Upgrade your plan to add more runs and continue operations without interruption.`
    }

    return reason
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Upgrade Required"
      showBackground={true}
    >
      <StyledDialogContent>
        <StyledHeader>
          <StyledIconContainer>
            <StyledUpgradeIcon icon={faGem} />
          </StyledIconContainer>

          <Typography variant="h5" sx={{
            textAlign: 'center',
            fontWeight: 700,
            color: 'text.primary',
            mb: 1,
          }}>
            {feature} Requires Plan Upgrade
          </Typography>
        </StyledHeader>

        <StyledInfoCard>
          <StyledInfoIconContainer>
            <Icon icon={faInfoCircle} />
          </StyledInfoIconContainer>
          <StyledInfoContent>
            <Typography variant="body1" sx={{ lineHeight: 1.5 }}>
              {getUpgradeMessage()}
            </Typography>
          </StyledInfoContent>
        </StyledInfoCard>

        {subscription && (
          <StyledCurrentPlanCard>
            <StyledPlanHeader>
              <Typography variant="subtitle2" sx={{
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: pxToRem(12),
              }}>
                Current Plan
              </Typography>
            </StyledPlanHeader>

            <StyledPlanDetails>
              <StyledPlanItem>
                <Typography variant="body2" color="text.secondary">
                  Tier
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {subscription.tierName}
                </Typography>
              </StyledPlanItem>

              {showVehicleLimit && (
                <StyledPlanItem>
                  <Typography variant="body2" color="text.secondary">
                    Vehicle Usage
                  </Typography>
                  <StyledUsageBadge>
                    {subscription.currentVehicleCount}/{subscription.vehicleQuantity}
                  </StyledUsageBadge>
                </StyledPlanItem>
              )}
            </StyledPlanDetails>
          </StyledCurrentPlanCard>
        )}

        <StyledActionContainer>
          {subscription?.stripeSubscriptionId ? (
            <Button
              primary
              onClick={handleManageSubscription}
              startIcon={<Icon icon={faGem} />}
              sx={{
                minWidth: pxToRem(200),
                height: pxToRem(44),
                fontWeight: 600,
              }}
            >
              Manage Subscription
            </Button>
          ) : (
            <Button
              primary
              onClick={handleUpgrade}
              startIcon={<Icon icon={faGem} />}
              sx={{
                minWidth: pxToRem(200),
                height: pxToRem(44),
                fontWeight: 600,
              }}
              size={'large'}
            >
              Upgrade Plan
            </Button>
          )}
        </StyledActionContainer>
      </StyledDialogContent>
    </Modal>
  )
}

const StyledDialogContent = styled(Box)`
  min-width: ${pxToRem(480)};
  max-width: ${pxToRem(560)};
  padding: ${pxToRem(8)} 0 ${pxToRem(16)} 0;
  display: flex;
  flex-direction: column;
`

const StyledHeader = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${pxToRem(24)};
`

const StyledIconContainer = styled(Box)`
  width: ${pxToRem(80)};
  height: ${pxToRem(80)};
  border-radius: 50%;
  background: linear-gradient(135deg, ${(props) => props.theme.palette.warning.light}, ${(props) => props.theme.palette.warning.main});
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${pxToRem(16)};
  box-shadow: 0 4px 20px ${(props) => props.theme.palette.warning.main}25;
`

const StyledUpgradeIcon = styled(Icon)`
  font-size: ${pxToRem(36)};
  color: ${(props) => props.theme.palette.common.white};
`

const StyledInfoCard = styled(Box)`
  display: flex;
  align-items: flex-start;
  padding: ${pxToRem(20)};
  background-color: ${(props) => props.theme.palette.mode === 'dark'
    ? props.theme.palette.grey[900]
    : props.theme.palette.grey[50]};
  border-radius: ${pxToRem(12)};
  border-left: 4px solid ${(props) => props.theme.palette.info.main};
  margin-bottom: ${pxToRem(24)};
  gap: ${pxToRem(12)};
`

const StyledInfoIconContainer = styled(Box)`
  width: ${pxToRem(24)};
  height: ${pxToRem(24)};
  border-radius: 50%;
  background-color: ${(props) => props.theme.palette.info.main};
  color: ${(props) => props.theme.palette.common.white};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: ${pxToRem(2)};
  
  .MuiSvgIcon-root {
    font-size: ${pxToRem(14)};
  }
`

const StyledInfoContent = styled(Box)`
  flex: 1;
`

const StyledCurrentPlanCard = styled(Box)`
  padding: ${pxToRem(20)};
  background-color: ${(props) => props.theme.palette.background.default};
  border-radius: ${pxToRem(12)};
  border: 1px solid ${(props) => props.theme.palette.divider};
  margin-bottom: ${pxToRem(32)};
`

const StyledPlanHeader = styled(Box)`
  margin-bottom: ${pxToRem(16)};
  padding-bottom: ${pxToRem(8)};
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
`

const StyledPlanDetails = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(12)};
`

const StyledPlanItem = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledUsageBadge = styled(Typography)`
  background-color: ${(props) => props.theme.palette.warning.light};
  color: ${(props) => props.theme.palette.warning.contrastText};
  padding: ${pxToRem(4)} ${pxToRem(12)};
  border-radius: ${pxToRem(16)};
  font-size: ${pxToRem(13)};
  font-weight: 600;
`

const StyledActionContainer = styled(Box)`
  display: flex;
  justify-content: center;
  width: 100%;
`